import { isInWeb3Mini } from '@web3/utils';
import { debugLog } from '../utils/common';
import { walletIdMap } from './../utils/constants';
import { Web3ProviderConnector } from './connectors/evm/Web3ProviderConnector';
import { get6963Wallets, harvestWalletHub } from './connectors/evm/eip6963';
import { WalletHub } from './core/WalletHub';
import { WalletHubCenter } from './core/WalletHubCenter';
import { ChainTypeList } from './core/types';
import { bybitExtension, BybitExtensionHub } from './hubs/BybitExtensionHub';
import { bybitWcConnectHub, evmWalletConnectHub } from './hubs/WalletConnectHub';
import { isBrowser } from './misc';
import { autoFindConstructors } from './hubs/autoFindConstructors';

type WalletExtension = {
  // 这个钱包是什么类型的钱包：可选，默认EVM，其它：BTC、SOL、SUI；但是暂时都不支持
  type?: ChainTypeList;
  // 钱包名字（界面上会展示）
  name: string;
  // 图标（界面上有可能会用）
  icon: string;
  // 钱包标识（唯一区分该钱包的标识，填写钱包的 rdns，不然选择界面可能会出现两个一样的钱包（因为有一个是通过自动发现发现的）
  id: string;
  // window 对象上的变量名，例如：window.ethereum;
  varName: string | (() => any);
  // 没有安装时，跳转的安装链接
  installUrl?: string;
};

export type InitWalletHubCenterOptions = {
  // 是否激活自动发现钱包,默认true
  enableDiscovery?: boolean;
  // 开始自动发现钱包前的一个延迟，用于预防某些钱包对eip6963 等事件监听慢的问题，默认300ms
  autoDiscoverDelay?: number;
  // 用于存储用户当前连接到的第三方钱包的local Storage key，默认：web3-current-wallet
  activeHubStorageKey?: string;
  // 是否防止MetaMask或其他钱包之间互相劫持,默认true
  avoidWalletHijack?: boolean;
  // 固定钱包
  fixedWallet?: WalletExtension[];
};

export enum WalletTag {
  // 扫描出来的
  scan = 'scan',
  // 业务通过配置预设的
  config = 'config',
}

// 预置的钱包
const innerWalletHubs = [bybitExtension, evmWalletConnectHub, bybitWcConnectHub];

class Web3WalletHubCenter extends WalletHubCenter {
  // 是否是 Bybit 插件钱包
  public get isBybitWalletExtension(): boolean {
    return this.activeWalletHub?.id === walletIdMap.bybit;
  }
  public get isBybitWalletConnect() {
    return this.activeWalletHub?.id === walletIdMap.bybitWalletConnect;
  }
  /**
   * 是否是 Bybit 品牌系的钱包（用于logo展示之类的)
   */
  public get isBybitBrand(): boolean {
    return this.isBybitWalletExtension || this.isBybitWalletConnect;
  }
  // 是否是wallet connect
  public get isBrandWalletConnect() {
    return this.activeWalletHub?.id === walletIdMap.bybitWalletConnect;
  }

  public get bybitExtension() {
    return this.getWalletHub(walletIdMap.bybit) as BybitExtensionHub | undefined;
  }
}

/**
 * 这里的初始化，确实非常曲折，因为对象是先new 出来的，option 本来已经在对象里确定了
 * wallet init的过程是在 WalletHubCenter 里的init 方法里面完成的（本来的设计），其中一个环节是异步的
 * 所以这里 比较迂回的方式是 将opt 用一个promise 去等待异步设置，然后再在初始化的步骤中等待这个 配置 设置完成
 * 然后 WalletHubCenter 继续完成 init 操作，不影响后续所有的 loading 或 ready等状态变化
 */
export let setWeb3WalletHubCenterOpt: (opts?: InitWalletHubCenterOptions) => void;

const optPromise = new Promise<InitWalletHubCenterOptions | undefined>((resolve) => {
  setWeb3WalletHubCenterOpt = resolve;
});

const getWalletHubCenter = () => {
  const setOptionAndReturnHubs = async () => {
    const opts = (await optPromise) || {};
    const { autoDiscoverDelay = 300, activeHubStorageKey, fixedWallet = [], avoidWalletHijack = true, enableDiscovery = true } = opts;

    if (activeHubStorageKey && typeof activeHubStorageKey === 'string') {
      center.option.activeWalletHubStorageKey = activeHubStorageKey;
    }
    const configWallets = fixedWallet
      .map(({ name, id, icon, installUrl, varName, type = 'EVM' }) => {
        if (type !== 'EVM') {
          // TODO：暂时不支持其他钱包的配置和连接
          return undefined;
        }
        return new WalletHub({
          name,
          id,
          icon,
          installUrl,
          tags: [WalletTag.config],
          connectors: [new Web3ProviderConnector(id, varName as string)],
        });
      })
      .filter(Boolean) as WalletHub[];

    const presetWalletHubs = innerWalletHubs.concat(configWallets);
    // 这里这个600毫秒，是在开发者电脑上测试得到 的差不多最快得到钱包eip6963反馈的时间
    const [callback, scannedHubsPromise] = harvestWalletHub(600, {
      infoFilter: ({ detail: { info, provider } }) => {
        const metaMaskHubIndex = presetWalletHubs.findIndex((w) => w.id === info.rdns);
        if (metaMaskHubIndex < 0) {
          // 没找到钱包，可以做后续处理
          return true;
        }
        const originalHub = presetWalletHubs[metaMaskHubIndex];

        // 防止 固定化的钱包被其他钱包給劫持，就没法连接该钱包。主要是针对Metamask
        // bybit插件除外，（因为bybit插件这边用的另外一个类，这里就不专门处理了，一般也不会专门针对bybitWallet这个变量劫持
        if (avoidWalletHijack && originalHub.id !== walletIdMap.bybit) {
          // 还没有连接的时候，甚至初始化的时候才替换，避免万一有状态上的不一致
          if (!originalHub.connectors?.length) {
            presetWalletHubs[metaMaskHubIndex] = new WalletHub({
              name: originalHub.name,
              id: originalHub.id,
              icon: originalHub.icon,
              installUrl: originalHub.installUrl,
              tags: originalHub.tags,
              connectors: [new Web3ProviderConnector(originalHub.id, provider)],
            });
          }
        }
        // 这下面都是找到了钱包，不需要再专门生成wallet hub
        return false;
      },
      infoHandler: ({ detail }) => {
        const { info, provider } = detail;
        if (autoFindConstructors[info.rdns]) {
          return autoFindConstructors[info.rdns](detail);
        }
        return new WalletHub({
          name: info.name,
          icon: info.icon,
          id: info.rdns,
          tags: [WalletTag.scan],
          connectors: [new Web3ProviderConnector(info.rdns, provider)],
        });
      },
      timeoutItemCallback: (hub) => {
        if (enableDiscovery) {
          debugLog('timeout add wallet', hub);
          center.addWalletHub(hub);
        }
      },
    });

    get6963Wallets(callback, autoDiscoverDelay);

    const scannedHubs = await scannedHubsPromise;
    debugLog('自动发现的钱包', scannedHubs);
    return enableDiscovery ? presetWalletHubs.concat(scannedHubs) : presetWalletHubs;
  };

  const center = new Web3WalletHubCenter({
    getWalletHubs: setOptionAndReturnHubs,
    // 如果是小程序，默认就直接用bybit 插件恢复钱包
    getInitialWalletHub: async (stored?: string) => {
      if (isInWeb3Mini()) {
        return bybitExtension.id;
      }
      return stored;
    },
  });
  return center;
};

export const thirdPartyWallet = getWalletHubCenter();

// 这个是为了适配web3 基座 wagmi的 断连
thirdPartyWallet.on('walletHubChange', (newHub, oldHub) => {
  if (!isBrowser) {
    return;
  }
  if (newHub) {
    localStorage.removeItem(`wagmi.${newHub.id}.disconnected`);
  }
  if (oldHub) {
    localStorage.setItem(`wagmi.${oldHub.id}.disconnected`, 'true');
  }
});
